import { Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { TextValidator } from "anketa-core/validation";
import {
  AnketaCheckPermissionOptions,
  AnketaResourcesEnum,
} from "../../../../";
import { QuestionsRightsTypes } from "..";
// Seeded values come from the parser's defaults rather than being written out
// again here. Two copies of "400 € a month, daily = /30, alert at 90%" would
// drift the first time one of them changed.
import {
  AI_DAILY_DIVISOR,
  AI_SOFT_RATIO,
  AI_WEEKLY_DIVISOR,
  getDefaultAiSettings,
} from "src/helper";

/**
 * One budget window (daily / weekly / monthly).
 *
 * Three of these run CONCURRENTLY on the same gateway: LiteLLM tracks and resets
 * each independently, so there is no hierarchy to model here - just three
 * numbers. One gateway is deployed per customer VPC, so the budget owner IS the
 * VPC.
 *
 * The MONTHLY window is the anchor. It is the figure actually agreed with a
 * customer, so it is the only one that REJECTS at its cap; the daily and weekly
 * windows are derived from it (monthly/30, monthly/4) to catch a runaway day or
 * week early, and a limit nobody negotiated should not be able to take AI away.
 *
 * `seed` reads the parser's defaults, so what the questionnaire pre-fills and
 * what an unanswered questionnaire parses to are the same numbers by
 * construction.
 */
interface BudgetWindowSpec {
  key: "daily" | "weekly" | "monthly";
  en: string;
  de: string;
  deNoun: string;
  helpEn: string;
  helpDe: string;
}

const DEFAULTS = getDefaultAiSettings().budgets;

const SOFT_PERCENT = Math.round(AI_SOFT_RATIO * 100);

const WINDOWS: BudgetWindowSpec[] = [
  {
    key: "daily",
    en: "Daily limit (€)",
    de: "Tageslimit (€)",
    deNoun: "Tages",
    helpEn: `Spend cap per day. Derived from the monthly limit (monthly ÷ ${AI_DAILY_DIVISOR}) unless you set it explicitly - it exists to catch a runaway day early, so it alerts rather than rejecting.`,
    helpDe: `Ausgabenobergrenze pro Tag. Wird aus dem Monatslimit abgeleitet (Monat ÷ ${AI_DAILY_DIVISOR}), sofern nicht explizit gesetzt - dient der Früherkennung eines Ausreißertags und warnt daher, statt abzuweisen.`,
  },
  {
    key: "weekly",
    en: "Weekly limit (€)",
    de: "Wochenlimit (€)",
    deNoun: "Wochen",
    helpEn: `Spend cap per 7 days. Derived from the monthly limit (monthly ÷ ${AI_WEEKLY_DIVISOR}) unless you set it explicitly, and alerts rather than rejecting for the same reason.`,
    helpDe: `Ausgabenobergrenze pro 7 Tage. Wird aus dem Monatslimit abgeleitet (Monat ÷ ${AI_WEEKLY_DIVISOR}), sofern nicht explizit gesetzt, und warnt aus demselben Grund, statt abzuweisen.`,
  },
  {
    key: "monthly",
    en: "Monthly limit (€)",
    de: "Monatslimit (€)",
    deNoun: "Monats",
    helpEn:
      "Spend cap per calendar month - it resets on the 1st, the way the invoice does. THE figure to agree with the customer: the daily and weekly windows are derived from it, and this is the one that actually rejects requests at its cap.",
    helpDe:
      "Ausgabenobergrenze pro Kalendermonat - Reset am 1., wie bei der Rechnung. DER mit dem Kunden zu vereinbarende Wert: Tages- und Wochenfenster werden daraus abgeleitet, und dieses Fenster weist Anfragen am Limit tatsächlich ab.",
  },
];

/**
 * AI/ML gateway settings: spend budgets in euro and cost alerting.
 *
 * INTERNAL ONLY, like the Features section this hangs off - these are commercial
 * controls, not customer-facing configuration.
 *
 * Alert DESTINATIONS are deliberately absent. A RocketChat hook URL is a bearer
 * credential and an SMTP password obviously is; the fact table is neither
 * encrypted nor scoped to superusers, so they are injected at render time from
 * the environment (`enrichWithAiAlerting`) and the per-customer destinations
 * live in their own admin-managed table.
 *
 * Returned as a factory, never a module-level singleton: OpsanioCatalog rebuilds
 * its sections fresh for every questionnaire, and a shared Section instance
 * throws "has already a parent" on the second build.
 */
export const aiSection = (
  questionsRights: (
    q: QuestionsRightsTypes,
    options?: AnketaCheckPermissionOptions<AnketaResourcesEnum.questionnaires>,
  ) => void,
  setDefaults: boolean,
): Section => {
  const ai = new Section("ai", [
    new Translation("en", "AI / ML"),
    new Translation("de", "AI / ML"),
  ]);
  ai.description = [
    new Translation(
      "en",
      `
**Spend limits for the AI gateway**

All amounts are in **euro**. The model prices this cluster is billed at are declared in euro, so the gateway's own spend ledger, the limits below and the customer's invoice are the same currency.

The three windows run **at the same time** on the same gateway, each tracked and reset independently - a daily, a weekly and a monthly cap coexist. One gateway is deployed per VPC, so these limits belong to this customer alone.

**The monthly limit is the one to agree with the customer.** The daily and weekly caps are derived from it (÷ 30 and ÷ 4) unless you fill them in explicitly, so raising the monthly figure moves all three.

**Hard limit vs. alert threshold**

- The **limit** is the cap for that window. Whether reaching it *rejects* requests or merely *alerts* is the "Reject requests..." answer under each window.
- By default **only the monthly window rejects**. The shorter windows are derived rather than negotiated, so they warn early instead of taking AI away for the rest of the day or week.
- The **alert threshold** is a lower figure at which a warning is sent while requests still succeed. It must stay below the limit - a warning that arrives at the cap arrives too late to act on.

The gateway also forecasts: it warns when the current burn rate will breach the threshold *before the window resets*, rather than only reporting after the money is gone.`,
    ),
    new Translation(
      "de",
      `
**Ausgabenlimits für das AI-Gateway**

Alle Beträge in **Euro**. Die Modellpreise, mit denen dieser Cluster abgerechnet wird, sind in Euro hinterlegt - das Spend-Ledger des Gateways, die Limits unten und die Kundenrechnung sind also dieselbe Währung.

Die drei Zeitfenster laufen **gleichzeitig** auf demselben Gateway und werden jeweils unabhängig gezählt und zurückgesetzt - Tages-, Wochen- und Monatsgrenze bestehen nebeneinander. Pro VPC wird ein Gateway betrieben, diese Limits gelten also ausschließlich für diesen Kunden.

**Das Monatslimit ist der mit dem Kunden zu vereinbarende Wert.** Tages- und Wochenlimit werden daraus abgeleitet (÷ 30 bzw. ÷ 4), sofern sie nicht explizit gefüllt werden - eine Erhöhung des Monatswerts verschiebt also alle drei.

**Hartes Limit vs. Warnschwelle**

- Das **Limit** ist die Obergrenze des jeweiligen Fensters. Ob das Erreichen Anfragen *abweist* oder nur *warnt*, steuert die Frage "Anfragen abweisen..." unter jedem Fenster.
- Standardmäßig weist **nur das Monatsfenster** ab. Die kürzeren Fenster sind abgeleitet und nicht vereinbart, warnen daher früh, statt AI für den Rest des Tages oder der Woche zu entziehen.
- Die **Warnschwelle** ist ein niedrigerer Betrag, bei dem gewarnt wird, während Anfragen weiterhin funktionieren. Sie muss unter dem Limit liegen - eine Warnung am Limit kommt zu spät, um noch zu reagieren.

Das Gateway prognostiziert zusätzlich: es warnt, wenn die aktuelle Verbrauchsrate die Schwelle **vor** dem Reset des Fensters reißen wird, statt erst zu melden, wenn das Geld weg ist.`,
    ),
  ];
  // Commercial controls: internal staff only, never the customer. Mirrors the
  // Features section this section is conditional on.
  questionsRights(ai, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });

  const budgets = new Section("budgets", [
    new Translation("en", "Spend budgets"),
    new Translation("de", "Ausgabenbudgets"),
  ]);
  questionsRights(budgets, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  ai.addChild(budgets);

  const budgetsEnabled = new OptionsQuestion(
    "enabled",
    [
      new Translation("en", "Enforce spend budgets?"),
      new Translation("de", "Ausgabenbudgets durchsetzen?"),
    ],
    "default.options.yes_no",
  );
  budgetsEnabled.help = [
    new Translation(
      "en",
      "No leaves the gateway uncapped. Spend is still recorded and the provider-level backstop still applies, but nothing stops a runaway bill.",
    ),
    new Translation(
      "de",
      "Nein lässt das Gateway ohne Obergrenze. Ausgaben werden weiter erfasst und die providerseitige Notbremse gilt weiterhin, aber nichts stoppt eine ausufernde Rechnung.",
    ),
  ];
  questionsRights(budgetsEnabled, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  budgets.addChild(budgetsEnabled);

  const windowQuestions: Array<{
    spec: BudgetWindowSpec;
    limit: OpenQuestion;
    soft: OpenQuestion;
    enforce: OptionsQuestion;
  }> = [];

  WINDOWS.forEach((spec) => {
    const limit = new OpenQuestion(`${spec.key}_limit`, [
      new Translation("en", spec.en),
      new Translation("de", spec.de),
    ]);
    limit.help = [
      new Translation("en", spec.helpEn),
      new Translation("de", spec.helpDe),
    ];
    limit.validator = new TextValidator();
    questionsRights(limit, {
      isInternal: true,
      isExternal: false,
      hideForExternal: true,
    });
    budgets.addChild(limit);

    const soft = new OpenQuestion(`${spec.key}_soft`, [
      new Translation("en", `${spec.en.replace(" (€)", "")} - alert at (€)`),
      new Translation("de", `${spec.de.replace(" (€)", "")} - Warnung bei (€)`),
    ]);
    soft.help = [
      new Translation(
        "en",
        `Alert fires here while requests still succeed. Must be below the limit; left empty, ${SOFT_PERCENT}% of the limit is used.`,
      ),
      new Translation(
        "de",
        `Warnung wird hier ausgelöst, während Anfragen weiterhin funktionieren. Muss unter dem Limit liegen; leer bedeutet ${SOFT_PERCENT}% des Limits.`,
      ),
    ];
    soft.validator = new TextValidator();
    questionsRights(soft, {
      isInternal: true,
      isExternal: false,
      hideForExternal: true,
    });
    budgets.addChild(soft);

    const enforce = new OptionsQuestion(
      `${spec.key}_enforce`,
      [
        new Translation(
          "en",
          `Reject requests when the ${spec.key} limit is reached?`,
        ),
        new Translation(
          "de",
          `Anfragen abweisen, wenn das ${spec.deNoun}limit erreicht ist?`,
        ),
      ],
      "default.options.yes_no",
    );
    enforce.help = [
      new Translation(
        "en",
        "Yes rejects requests at the cap. No alerts and keeps serving. Default: Yes for the monthly window only - that is the figure agreed with the customer, whereas the daily and weekly caps are derived from it and are there to warn early.",
      ),
      new Translation(
        "de",
        "Ja weist Anfragen am Limit ab. Nein warnt und liefert weiter. Standard: Ja nur für das Monatsfenster - das ist der mit dem Kunden vereinbarte Wert, während Tages- und Wochenlimit daraus abgeleitet sind und der Frühwarnung dienen.",
      ),
    ];
    questionsRights(enforce, {
      isInternal: true,
      isExternal: false,
      hideForExternal: true,
    });
    budgets.addChild(enforce);

    windowQuestions.push({ spec, limit, soft, enforce });
  });

  const alerting = new Section("alerting", [
    new Translation("en", "Cost alerting"),
    new Translation("de", "Kostenalarmierung"),
  ]);
  alerting.description = [
    new Translation(
      "en",
      "Where budget events go. The gateway computes them on every request regardless; this decides whether anyone hears about them. Leave the webhook URL empty to use the default room configured under Admin > AI cost alerts.",
    ),
    new Translation(
      "de",
      "Wohin Budget-Ereignisse gehen. Das Gateway berechnet sie ohnehin bei jeder Anfrage; hier wird entschieden, ob sie jemand erfährt. Die Webhook-URL leer lassen, um den unter Administration > AI-Kostenalarme konfigurierten Standardraum zu verwenden.",
    ),
  ];
  questionsRights(alerting, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  ai.addChild(alerting);

  const alertingEnabled = new OptionsQuestion(
    "enabled",
    [
      new Translation("en", "Send cost alerts?"),
      new Translation("de", "Kostenalarme senden?"),
    ],
    "default.options.yes_no",
  );
  alertingEnabled.help = [
    new Translation(
      "en",
      "No means budget events are computed and discarded - which is the state every gateway was in before this existed.",
    ),
    new Translation(
      "de",
      "Nein bedeutet, dass Budget-Ereignisse berechnet und verworfen werden - genau der Zustand, in dem jedes Gateway vor dieser Funktion war.",
    ),
  ];
  questionsRights(alertingEnabled, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  alerting.addChild(alertingEnabled);

  const webhookUrl = new OpenQuestion("webhook_url", [
    new Translation("en", "Cost alert webhook URL"),
    new Translation("de", "Webhook-URL für Kostenalarme"),
  ]);
  webhookUrl.help = [
    new Translation(
      "en",
      "Where THIS customer's budget alerts are posted, e.g. a RocketChat incoming hook (`https://chat.example.net/hooks/...`). Must be https. Leave empty to use the default room from Admin > AI cost alerts. Note: this URL is a credential and is stored with the questionnaire answers, so anyone who can read this questionnaire can read it - keep sensitive rooms on the admin-managed default.",
    ),
    new Translation(
      "de",
      "Wohin die Budget-Alarme DIESES Kunden gepostet werden, z. B. ein RocketChat Incoming Hook (`https://chat.example.net/hooks/...`). Muss https sein. Leer lassen, um den Standardraum aus Administration > AI-Kostenalarme zu verwenden. Hinweis: Diese URL ist ein Zugangstoken und wird bei den Fragebogen-Antworten gespeichert - wer den Fragebogen lesen kann, kann sie lesen. Sensible Räume daher über den administrativen Standard konfigurieren.",
    ),
  ];
  webhookUrl.validator = new TextValidator();
  questionsRights(webhookUrl, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  alerting.addChild(webhookUrl);

  const receiverEmails = new OpenQuestion("receiver_emails", [
    new Translation("en", "Cost alert e-mail recipients"),
    new Translation("de", "E-Mail-Empfänger für Kostenalarme"),
  ]);
  receiverEmails.help = [
    new Translation(
      "en",
      "Who is e-mailed about this customer's budget events, comma-separated. Leave empty to use the default recipients from the environment. Entries that are not e-mail addresses are ignored - one malformed address can make a mail server reject the message for everyone on the list.",
    ),
    new Translation(
      "de",
      "Wer über die Budget-Ereignisse dieses Kunden per E-Mail informiert wird, kommagetrennt. Leer lassen, um die Standardempfänger aus der Umgebung zu verwenden. Einträge, die keine E-Mail-Adressen sind, werden ignoriert - eine fehlerhafte Adresse kann dazu führen, dass ein Mailserver die Nachricht für alle auf der Liste ablehnt.",
    ),
  ];
  receiverEmails.validator = new TextValidator();
  questionsRights(receiverEmails, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  alerting.addChild(receiverEmails);

  const reportFrequency = new OpenQuestion("report_frequency", [
    new Translation("en", "Cost digest interval"),
    new Translation("de", "Intervall der Kostenübersicht"),
  ]);
  reportFrequency.help = [
    new Translation(
      "en",
      "Periodic spend report, e.g. `7d` or `30d`. This is what answers “what did this cost last week” without anyone crossing a threshold. Empty disables it.",
    ),
    new Translation(
      "de",
      "Periodischer Ausgabenbericht, z. B. `7d` oder `30d`. Damit wird „was hat das letzte Woche gekostet“ beantwortet, ohne dass eine Schwelle gerissen wird. Leer deaktiviert ihn.",
    ),
  ];
  reportFrequency.validator = new TextValidator();
  questionsRights(reportFrequency, {
    isInternal: true,
    isExternal: false,
    hideForExternal: true,
  });
  alerting.addChild(reportFrequency);

  if (setDefaults) {
    // yes_no: index 0 is Yes, 1 is No.
    budgetsEnabled.setFactValue(0);
    alertingEnabled.setFactValue(0);
    reportFrequency.setFactValue("7d");
    windowQuestions.forEach(({ spec, limit, soft, enforce }) => {
      const window = DEFAULTS[spec.key];
      limit.setFactValue(`${window.limit}`);
      soft.setFactValue(`${window.soft}`);
      // yes_no: 0 is Yes, 1 is No. Only the monthly window defaults to Yes.
      enforce.setFactValue(window.enforce ? 0 : 1);
    });
  }

  return ai;
};
